/* eslint-disable eqeqeq */
import { useRef, useState } from 'react';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm, type AccessoryFormApi } from '../useAccessoryForm';
import { ahuBoardBus } from '../../../bus/ahuBoardBus';
import { accList } from '../accessoryFieldKit';
import type { MountingDistances } from '../mountingMath';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-2 — AMS form: STRUCTURAL PORT ONLY (parity 336).
 *
 * Ground truth found at port time (re-verified against origin/dev):
 *  - accessories/ams/view/form.js (297) + view/mounting_popup.js (198) exist,
 *    but app.js has NO `view.accessory.ams.form` alias, tab.accessories.js
 *    never references 'ams', and `accessories/ams/tmpl/` DOES NOT EXIST on
 *    disk — the legacy require.text of form.tmpl/mounting_popup.tmpl would
 *    404 if the view were ever constructed. The ams form layer is
 *    dead-by-unreachability in the live tree; dev3 has ZERO ams rows.
 *  - `model.searchAMSesByParams` / `masterAMSCollection` / `params_ams` are
 *    referenced by form.js but defined NOWHERE in the repo (the model at
 *    accessories/ams/model/ams.js carries none of them).
 *
 * What this port preserves (the legacy Backbone.View semantics):
 *  - EXCLUSIVE subtabs — opening one UNCHECKS all others (subtabOpenClose
 *    override, unlike the base multi-open behavior);
 *  - params_ams filter model + amses_reset clearing it + re-search;
 *  - `rotate:canvas:ams:table {collection, cms, name:'ams', accessory_id}`
 *    flip-table payload (§11.1 receiver);
 *  - blur-based model-field commits (events-table form) + select parseInt;
 *  - position_x/position_y → x/y rename on position commits (parity 336);
 *  - own per-type mounting popup: plane radios + dist inputs with CANCEL
 *    (restore initial mounting state) and CONFIRM (recalc_position + apply
 *    staged model_field_popup values) — the two-button protocol that differs
 *    from the base popup's single Done.
 *
 * Markup is a minimal reconstruction in the §52.1 grammar — the legacy
 * templates do not exist to copy.
 */
const config = ACCESSORY_FORM_CONFIGS.ams;

const AMS_SUBTABS = ['properties', 'mounting', 'remarks'] as const;

/** Structural stand-in for model.searchAMSesByParams (defined nowhere in the
 * legacy repo): keeps the masterAMSCollection empty and re-renders. */
function searchAMSesByParams(_params: Record<string, unknown>): Array<Record<string, unknown>> {
  return [];
}

const AmsMountingPopup = ({ api, open, onClose }: { api: AccessoryFormApi; open: boolean; onClose: () => void }) => {
  const { mounting, model, position } = api;
  const [staged, setStaged] = useState<Record<string, unknown>>({});
  const initial = useRef<Record<string, unknown> | null>(null);
  if (open && initial.current === null && mounting) {
    initial.current = mounting.toJSON();
  }
  if (!open || !mounting || !model) return null;

  const restore = () => {
    const s = initial.current as (Record<string, unknown> & Partial<MountingDistances>) | null;
    if (s) {
      mounting.set({
        plane_id: (s.plane_id as number | null) ?? null,
        plane_value: (s.plane_value as string | null) ?? null,
        distance_to_floor: Number(s.distance_to_floor) || 0,
        distance_to_roof: Number(s.distance_to_roof) || 0,
        distance_to_wall_left: Number(s.distance_to_wall_left) || 0,
        distance_to_wall_right: Number(s.distance_to_wall_right) || 0,
        distance_to_wall_upstream: Number(s.distance_to_wall_upstream) || 0,
        distance_to_wall_downstream: Number(s.distance_to_wall_downstream) || 0,
      });
    }
  };
  const close = () => {
    initial.current = null;
    setStaged({});
    onClose();
  };

  return (
    <div id={`popup_accessory_mounting_${model.id}`} className="popup-wrapper location-popup active">
      <div className="popup-content popup-content-animation">
        <h2 className="popup-heading">Mounting</h2>
        <form>
          <div className="plane-content">
            {accList(api.cmsAccessory, 'mounting_plane').map((p) => (
              <label key={String(p.id)} className={String(p.value ?? '')}>
                <input
                  type="radio"
                  name="plane"
                  value={String(p.value ?? '')}
                  checked={mounting.plane_id == p.id}
                  onChange={() => {
                    model.markUserAction();
                    position?.markUserAction();
                    mounting.set({ plane_id: Number(p.id), plane_value: String(p.value ?? '') });
                  }}
                />
                <span>{String(p.name ?? '')}</span>
              </label>
            ))}
          </div>
          <div className="selected-position">
            {(
              [
                'distance_to_floor',
                'distance_to_roof',
                'distance_to_wall_left',
                'distance_to_wall_right',
                'distance_to_wall_upstream',
                'distance_to_wall_downstream',
              ] as const
            ).map((name) => (
              <input
                key={`${name}_${String(mounting.distances[name])}`}
                className={`dist-input dist-to-${name.replace('distance_to_', '').replace('wall_', 'wall_')}`}
                data-step="0.01"
                data-min="0"
                type="text"
                data-type="number"
                autoComplete="off"
                name={name}
                defaultValue={String(mounting.distances[name] ?? '')}
                onBlur={(e) => {
                  model.markUserAction();
                  position?.markUserAction();
                  mounting.set({ [name]: Number.parseFloat(e.currentTarget.value) });
                }}
              />
            ))}
          </div>
        </form>
        <div className="popup-control-buttons">
          <button
            type="button"
            className="btn"
            name="confirm"
            onClick={() => {
              // confirm: recalc_position + apply staged model fields, then
              // snapshot the new state (mounting_popup.js:133-141).
              model.markUserAction();
              position?.markUserAction();
              mounting.recalc_position();
              if (Object.keys(staged).length) model.set(staged);
              close();
            }}
          >
            Confirm
          </button>
          <button
            type="button"
            className="btn"
            name="cancel"
            onClick={() => {
              // cancel: restore the initial mounting state (no position write).
              restore();
              close();
            }}
          >
            Cancel
          </button>
        </div>
        {/* model_field_popup staging slot — populated when real ams CMS
            special parameters exist (none on dev3; structural). */}
        <div className="special-parameters-container" data-staged={Object.keys(staged).length}
          onChange={() => setStaged(staged)} />
      </div>
    </div>
  );
};

const AmsForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting', 'remarks'],
  });
  // EXCLUSIVE subtabs — ams overrides the base multi-open behavior.
  const [openSubtab, setOpenSubtab] = useState<string | null>(null);
  const [paramsAms, setParamsAms] = useState<Record<string, unknown>>({});
  const [masterAMSes, setMasterAMSes] = useState<Array<Record<string, unknown>>>([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const amsesCount = masterAMSes.length;

  const toggleExclusive = (name: string, checked: boolean) => {
    setOpenSubtab(checked ? name : null);
  };
  const search = (params: Record<string, unknown>) => {
    setMasterAMSes(searchAMSesByParams(params));
  };
  const commitBlurField = (name: string, raw: string, numeric: boolean) => {
    model.markUserAction();
    model.set({ [name]: numeric ? Number(raw.replace(/,/g, '')) : raw });
  };
  const commitPositionRenamed = (name: string, raw: string) => {
    // position_x/position_y → x/y rename (form.js:239-254 / parity 336).
    const key = name === 'position_x' ? 'x' : name === 'position_y' ? 'y' : name;
    api.position?.markUserAction();
    api.position?.set({ [key]: +raw });
  };

  return (
    <>
      <form ref={(el) => { api.formRef.current = el; }}>
        <ul className="inputs-group sublist">
          {AMS_SUBTABS.map((name) => (
            <li key={name} className={name === 'mounting' ? 'mounting-section-wrapper' : undefined}>
              <input
                id={`ams_${cid}_${name}`}
                data-subtab={name}
                type="checkbox"
                checked={openSubtab === name}
                onChange={(e) => toggleExclusive(name, e.target.checked)}
              />
              <label htmlFor={`ams_${cid}_${name}`}>
                {name.charAt(0).toUpperCase() + name.slice(1)}
                <i className="icon-add" />
              </label>
              <ul className="sublist">
                {name === 'properties' && (
                  <>
                    <li>
                      <div>
                        <input
                          name="tag"
                          className="model_field text-left"
                          type="text"
                          defaultValue={String(model.get('tag') ?? '')}
                          onBlur={(e) => commitBlurField('tag', e.currentTarget.value, false)}
                        />
                        <span className="label">Tag</span>
                      </div>
                    </li>
                    <li>
                      <div className="single-line">
                        <select
                          name="model"
                          value={String(paramsAms.model ?? '0')}
                          onChange={(e) => {
                            // changeAMSParamModel: 0 → unset (form.js:178-195).
                            const value = e.target.value;
                            const next = { ...paramsAms };
                            if (value == '0' || !value) delete next.model;
                            else next.model = value;
                            setParamsAms(next);
                            search(next);
                          }}
                        >
                          <option value="0">- -</option>
                          {accList(api.cmsAccessory, 'model').map((o) => (
                            <option key={String(o.id)} value={String(o.id)}>
                              {String(o.name ?? o.model ?? '')}
                            </option>
                          ))}
                        </select>
                        <span className="label">Model</span>
                      </div>
                    </li>
                    <li>
                      <div className="single-line">
                        <button
                          type="button"
                          className="btn btn-sync"
                          onClick={() => {
                            // rotateCanvas (form.js:143-151) — §11.1 flip-table.
                            ahuBoardBus.emit('rotate:canvas:ams:table', {
                              collection: masterAMSes,
                              cms: api.cmsAccessory,
                              name: 'ams',
                              accessory_id: model.get('id'),
                            });
                          }}
                        >
                          <span className="amses_search_result search-result">{amsesCount}</span>
                          <i className="icon-list btn-icon-sync">Smartlist</i>
                        </button>
                        <span
                          className="icon-refresh-1 amses_reset reset-btn"
                          onClick={() => {
                            // amses_reset clears params + re-search (form.js:138-141).
                            setParamsAms({});
                            search({});
                          }}
                        >
                          Reset
                        </span>
                      </div>
                    </li>
                  </>
                )}
                {name === 'mounting' && (
                  <>
                    <li>
                      <div className="mounting-plane">
                        <div className="checkboxes-group mounting-btn">
                          <button
                            type="button"
                            className="icon-mounting-1 btn-location"
                            name="location_btn"
                            onClick={() => setPopupOpen(true)}
                          />
                        </div>
                        <span className="selection">
                          {api.currentPlane ? <span>{String(api.currentPlane.name ?? '')}</span> : null}
                        </span>
                        <select
                          name="mounting_plane_id"
                          className="position_field"
                          required
                          value={
                            api.position && Number(api.position.get('mounting_plane_id'))
                              ? String(api.position.get('mounting_plane_id'))
                              : 'None'
                          }
                          onChange={(e) => {
                            // changePositionSelectFiled: parseInt + set (no
                            // createPosition — ams positions pre-exist).
                            const v = parseInt(e.target.value, 10);
                            if (v) {
                              api.position?.markUserAction();
                              api.position?.set({ mounting_plane_id: v });
                            }
                          }}
                        >
                          <option value="None" className="hide">
                            - -
                          </option>
                          {api.mountingPlanes.map((p) => (
                            <option key={String(p.id)} value={String(p.id)}>
                              {String(p.name ?? '')}
                            </option>
                          ))}
                        </select>
                        <span className="label required">Mounting | Plane</span>
                      </div>
                    </li>
                    {(['position_x', 'position_y'] as const).map((pname) => (
                      <li key={pname}>
                        <div className="accessory-mounting-floor">
                          <span className="unit">in</span>
                          <input
                            type="text"
                            data-type="number"
                            data-min="0"
                            /* An inches position, and `commitPositionRenamed`
                               stores `+raw` — so the box is 2-dp like every
                               other position field on the board
                               (`MountingPopup`, `AccessoryMountingPopup`).
                               It is the only `data-type="number"` box in the
                               board's own tabs that declared no `data-step`,
                               and `useBoardNumberInputs` reads that attribute
                               to decide whether `.` is a legal character:
                               without it the guard would have taken this one
                               for an integer field and refused `12.5`. */
                            data-step="0.01"
                            autoComplete="off"
                            name={pname}
                            className="position_field medium-input"
                            defaultValue={String(api.position?.get(pname.slice(-1)) ?? '')}
                            onBlur={(e) => commitPositionRenamed(pname, e.currentTarget.value)}
                          />
                          <span className="label">
                            <span>{pname === 'position_x' ? 'Position X' : 'Position Y'}</span>
                          </span>
                        </div>
                      </li>
                    ))}
                  </>
                )}
                {name === 'remarks' && (
                  <li>
                    <div>
                      <textarea
                        className="textarea model_field"
                        name="remarks"
                        defaultValue={String(model.get('remarks') ?? '')}
                        onBlur={(e) => commitBlurField('remarks', e.currentTarget.value, false)}
                      />
                      <span className="label">Remarks</span>
                    </div>
                  </li>
                )}
              </ul>
            </li>
          ))}
        </ul>
      </form>
      <AmsMountingPopup api={api} open={popupOpen} onClose={() => setPopupOpen(false)} />
    </>
  );
};

export default AmsForm;
